class processingTimeNotice {

  constructor() {
    this.productId = null;
    this.customerId = null;
    this.currentVariantId = null;
    this.updating = false;
  }

  /**
   * Initializes the special order container logic and binds necessary events if the required elements are present.
   * - Checks for the presence of specific DOM elements.
   * - Updates the product variant ID.
   * - Binds event listeners.
   * - Triggers inventory check and updates shipping notice based on the product variant.
   *
   * @return {void} Does not return a value.
   */
  init() {
    if (!document.querySelector('.special-order-container')) return;

    if (document.querySelector('.template-product')) {
      this.productId = parseInt(document.querySelector('.product-form').dataset.productId);
      this.updateVariantId();
      this.bindEvents();
      this.runInventoryCheck();
      this.updateVariantShippingNotice();
    }

    if(typeof window.customerId != "undefined") {
      this.customerId = window.customerId;
    }
    else if(typeof document.querySelector('body').dataset.customerId != "undefined") {
      this.customerId = parseInt(document.querySelector('body').dataset.customerId);
    }
  }

  /**
   * Binds various event listeners to elements related to product purchase forms and quantity controls.
   * These event listeners trigger inventory checks and updates to maintain accurate stock and variant data.
   *
   * @return {void} This method does not return any value.
   */
  bindEvents() {
    let _this = this;

    if (document.querySelector('.product-purchase-form [name="id"]') != null) {
      document.querySelector('.product-purchase-form [name="id"]').addEventListener("change", (e) => {
          _this.updateVariantId();
          _this.runInventoryCheck(e);
      });
    }
    if (document.querySelector('.quantity-wrapper input[name="quantity"]') != null) {
      document.querySelectorAll('.quantity-wrapper input[name="quantity"]').forEach(function (el) {
        el.addEventListener("change", (e) => {
          _this.runInventoryCheck(e);
        });
      });
    }
    if (document.querySelector('.quantity-wrapper a') != null) {
      document.querySelectorAll('.quantity-wrapper a').forEach(function (el) {
        el.addEventListener("click", (e) => {
          setTimeout(function () {
            _this.runInventoryCheck(e);
          }, 50)
        });
      });
    }

    document.addEventListener('theme:cartchanged', (e) => {
      _this.runInventoryCheck(e);
    });
  }

  /**
   * Runs an inventory check for the current product and updates variant inventory quantities.
   *
   * @param {Event|null} event - The event object triggering the inventory check or null if no event is provided.
   * @return {void} This method does not return a value.
   */
  runInventoryCheck(event = null) {
    if (this.updating) return;
    let _this = this;

    if(!this.customerId) return;

    this.updating = true;

    _this.getProcessingTimeNotice(_this.productId).then(
      (response) => {
        _this.updateVariantSelectInventoryQuantity(response.data);

        fetch('/cart.js')
          .then(res => res.json())
          .then(cartData => {
            _this.changeCartItemsSpecialOrderStatuses(cartData, response.data, event);
          });
      },
      (error) => {
        console.log('error', error)
      }
    ).catch(error => {
      console.log('error', error)
    });
  }

  /**
   * Updates the `currentVariantId` property based on the value of a specific input field in the DOM.
   * It retrieves the value from an input element with the selector `.product-purchase-form [name="id"]`,
   * converts it to an integer, and assigns it to `currentVariantId`. If the element or its value is undefined,
   * `currentVariantId` is set to null.
   *
   * @return {void} This method does not return a value.
   */
  updateVariantId() {
    this.currentVariantId = typeof document.querySelector('.product-purchase-form [name="id"]') != "undefined" ? parseInt(document.querySelector('.product-purchase-form [name="id"]').value) : null;
  }

  /**
   * Retrieves the processing time notice for a given product ID.
   *
   * @param {string} productId - The unique identifier of the product.
   * @return {Promise} A Promise that resolves with an object containing the product's processing time notice or rejects with an error message and details.
   */
  getProcessingTimeNotice(productId) {

    if(!this.customerId) return;

    return new Promise(async (resolve, reject) => {
      if (!productId) {
        reject({
          data: null,
          message: 'Wrong/missing product ID',
          productId: null,
        });
      }

      MultiStorefront.init(window.api_base_url, window.auth_token);

      await MultiStorefront.get(`/inventory/${productId}`).then((response) => {

        if (typeof response.message != 'undefined') {
          reject({
            error: response,
            message: response.message,
            productId: productId,
          });
          return;
        }

        resolve({
          data: response,
          message: 'Success',
          productId: productId
        });
      })
    });
  }

  /**
   * Updates the shipping notice input field by toggling its 'name' attribute based on the provided parameter.
   *
   * @param {boolean|null} enableInput - Determines whether to enable or disable the input field.
   * If `false`, the input is disabled by removing its 'name' attribute.
   * If `true` or `null`, the 'name' attribute is restored using the default value.
   * @return {void} This method does not return a value.
   */
  updateVariantShippingNotice(enableInput = null) {
    let target = document.querySelector('.processing-time-notice-hidden-input');

    if (!target) return;

    let defaultTargetName = target.dataset.name;
    let targetName, targetName2;

    // disable the input by removing the name
    if (enableInput === false) {
      targetName = '';
    } else {
      // enable the input by restoring the name
      targetName = defaultTargetName;
    }
    target.setAttribute('name', targetName);
  }

  /**
   * Updates the variant inventory quantity and adjusts UI elements based on inventory availability.
   *
   * @param {Object} data - The inventory data for all variants. Keys represent variant IDs, and values contain inventory details.
   * @return {void} No return value.
   */
  updateVariantSelectInventoryQuantity(data) {
    let showNotice = false;
    let inputTarget = document.querySelector('.processing-time-notice-base-inventory');
    let noticeTarget = document.querySelector('.special-order-container');
    let isSpecialOrder = noticeTarget.classList.contains('is-special-order');
    let qty = parseInt(document.querySelector('[name="quantity"]').value);
    let baseQty = null;

    for (const [key, value] of Object.entries(data)) {
      let varId = parseInt(key);
      if (varId === this.currentVariantId) {
        showNotice = typeof value.base == "undefined" || value.base < 1 || value.base < qty;
        baseQty = value.base;
      }
    }

    if (isSpecialOrder) showNotice = true;

    if (baseQty) {
      inputTarget.value = baseQty;
    }

    if (noticeTarget) {
      if (showNotice) {
        noticeTarget.classList.remove('hidden');
        this.updateVariantShippingNotice(true);
      } else {
        noticeTarget.classList.add('hidden');
        this.updateVariantShippingNotice(false);
      }
    }

  }

  /**
   * Updates the special order statuses and baseline inventory data for cart items by evaluating
   * inventory information and item properties. Adjusts cart items as necessary, including updating
   * item properties, handling special order notices, and refreshing the cart when changes are made.
   *
   * @param {Object} response - Response object containing cart items and their properties.
   * @param {Object} inventoryData - Object containing inventory details to validate and update cart items.
   * @param {Event|null} [event=null] - Optional event object, used to dispatch cart change notifications.
   * @return {void}
   */
  changeCartItemsSpecialOrderStatuses(response, inventoryData, event = null) {
    if (!response) return;

    let _this = this;
    let specialOrder = false;
    let haveSpecialOrder = false;
    let updatedItems = [];
    let promises = [];

    response.items.forEach((item) => {

      // First update the base inventory property where needed and check if we have a special order item
      if (inventoryData.hasOwnProperty(item.id)) {
        if (item.properties.hasOwnProperty('_base_inventory')) {
          let itemBaseInventory = parseInt(item.properties['_base_inventory']);
          let realBaseInventory = parseInt(inventoryData[item.id].base);

          let properties = item.properties;
          let update = false;

          // check if the line item is already a special order
          if (properties.hasOwnProperty('Est. Delivery')) {
            haveSpecialOrder = true;
            specialOrder = properties['Est. Delivery'];
          }
            // or update line item if the line item quantity is larger than base inventory
            // or if the base inventory line item property is not up to date
          else if (item.quantity > realBaseInventory) {
            specialOrder = document.querySelector('.processing-time-notice-hidden-input').value;
            haveSpecialOrder = true;
            properties['Est. Delivery'] = specialOrder;
            update = true;
          }
          if (itemBaseInventory != realBaseInventory) {
            properties['_base_inventory'] = realBaseInventory;
            update = true;
          }

          if (update) {
            updatedItems.push(item.id);
            let promise = changeCartItem({
              id: item.key,
              quantity: item.quantity,
              properties: properties
            });
            promises.push(promise);
          }
        }
      }
    });

    if (haveSpecialOrder) {
      // add special order notice to all cart items
      response.items.forEach((item) => {

        let properties = item.properties;
        let update = false;

        // update line item if the est. delive isn't set, but should be,
        // or if the base inventory line item property is not up to date
        if (!updatedItems.includes(item.id) && !properties.hasOwnProperty('Est. Delivery')) {
          properties['Est. Delivery'] = specialOrder;
          update = true;
        }
        if (!updatedItems.includes(item.id) && inventoryData.hasOwnProperty(item.id)) {
          let itemBaseInventory = parseInt(item.properties['_base_inventory']);
          let realBaseInventory = parseInt(inventoryData[item.id].base);
          if(realBaseInventory != itemBaseInventory) {
            properties['_base_inventory'] = parseInt(inventoryData[item.id].base);
            update = true;
          }
        }

        if (update) {
          let promise = changeCartItem({
            id: item.key,
            quantity: item.quantity,
            properties: properties
          });
          promises.push(promise);
        }
      });
    } else {
      // remove special order notice from all cart items
      response.items.forEach((item) => {
        if (item.properties.hasOwnProperty('Est. Delivery')) {
          let properties = item.properties;
          properties['Est. Delivery'] = null;

          let promise = changeCartItem({
            id: item.key,
            quantity: item.quantity,
            properties: properties
          });

          promises.push(promise);
        }
      })
    }

    // when all changes are done, update the cart
    Promise.allSettled(promises).then(function (results) {
      setTimeout(function () {
        if (results.length > 0) {
          if (event && event.bubbles) document.dispatchEvent(new CustomEvent('theme:cartchanged', {
            bubbles: false,
            cancelable: true
          }));
          document.dispatchEvent(new CustomEvent('theme:open-cart-drawer', {bubbles: false, cancelable: false}));
        }
        _this.updating = false;
      }, 250);
    });

  }

}

let processingTimeNoticeClass = new processingTimeNotice();
processingTimeNoticeClass.init();